// ════════════════════════════════════════════════════════════════════════════
// LpaSelect — select primitivo (single value), dual-mode, a11y
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export interface LpaSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface LpaSelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "size" | "onChange"
> {
  options: LpaSelectOption[];
  size?: "sm" | "md" | "lg";
  invalid?: boolean;
  fullWidth?: boolean;
  /** Placeholder option (renderiza como `<option value="" disabled>`) */
  placeholder?: string;
  /** onChange controlado simples (recebe direto o valor) */
  onChange?: (value: string) => void;
  testId?: string;
}

const SIZE_PADDING: Record<NonNullable<LpaSelectProps["size"]>, string> = {
  sm: "5px 9px",
  md: "8px 10px",
  lg: "10px 12px",
};

const SIZE_FONT: Record<NonNullable<LpaSelectProps["size"]>, string> = {
  sm: "var(--lpa-text-sm)",
  md: "var(--lpa-text-lg)",
  lg: "var(--lpa-text-xl)",
};

const LpaSelect = React.forwardRef<HTMLSelectElement, LpaSelectProps>(function LpaSelect(
  {
    options,
    size = "md",
    invalid = false,
    fullWidth = true,
    placeholder,
    onChange,
    value,
    style,
    className,
    testId,
    ...rest
  },
  ref
) {
  return (
    <select
      ref={ref}
      value={value ?? ""}
      onChange={(e) => onChange?.(e.target.value)}
      data-testid={testId}
      aria-invalid={invalid || undefined}
      className={className}
      style={{
        width: fullWidth ? "100%" : undefined,
        padding: SIZE_PADDING[size],
        fontSize: SIZE_FONT[size],
        fontFamily: "var(--lpa-font-sans)",
        color: "var(--lpa-text)",
        background: "var(--lpa-bg)",
        border: `1px solid ${invalid ? "var(--lpa-danger)" : "var(--lpa-border)"}`,
        borderRadius: "var(--lpa-radius-md)",
        outline: "none",
        cursor: "pointer",
        transition: "border-color var(--lpa-tr-fast), box-shadow var(--lpa-tr-fast)",
        ...style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.borderColor = "var(--lpa-accent)";
        e.currentTarget.style.boxShadow = "0 0 0 3px var(--lpa-accent-soft)";
        rest.onFocus?.(e);
      }}
      onBlur={(e) => {
        e.currentTarget.style.borderColor = invalid ? "var(--lpa-danger)" : "var(--lpa-border)";
        e.currentTarget.style.boxShadow = "none";
        rest.onBlur?.(e);
      }}
      {...rest}
    >
      {placeholder && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value} disabled={opt.disabled}>
          {opt.label}
        </option>
      ))}
    </select>
  );
});

export default LpaSelect;
